
//  0 o 254 son todos los días de la semana
export function normalizeWeekdayMask(weekday: number): number {
  const mask = Number(weekday);
  if (mask === 0 || mask === 254) {
    return 254;
  }
  return mask;
}

export function secondsToHours(seconds: number): number {
  return Math.floor(seconds / 3600);
}

export function secondsToMinutes(seconds: number): number {
  return Math.floor(seconds / 60);
}
export function minutesToHours(minutes: number): number {
  return Math.floor(minutes / 60);
}

// Convierte el tiempo de formato HH:MM a minutos
export function timeToMinutes(time: string): number {
  const [hours, minutes] = time.split(':').map(Number);

  return hours * 60 + minutes;
}


// Convierte el tiempo de la API a formato HH:MM
export function timecfgToFormattedTime(timeCfg: any) {
  const type = (timeCfg?.type ?? '').toLowerCase() ?? 'fixed';
  const sec = Number(timeCfg?.offset_sec) || 0;

  if (type === 'fixed') {
    return {
      type,
      timeStr: getDateFromMinutes(Math.round(sec / 60)),
      offsetSign: '+',
      offsetTime: '00:00',
    };
  }

  const min = Math.round(sec / 60);
  return {
    type,
    timeStr: '08:00',
    offsetSign: min < 0 ? '-' : '+',
    offsetTime: getDateFromMinutes(Math.abs(min)),
  };
}


/** Timestamp Unix (segundos) → hora `HH:mm` (p. ej. orto/ocaso de astcal). */
export function formatUnixTime(unixSec: unknown, timeZone?: string, fallback = '--:--'): string {
  const ts = Number(unixSec);
  if (!Number.isFinite(ts) || ts <= 0) {
    return fallback;
  }

  const parts = getTimePartsFromDate(new Date(ts * 1000), timeZone);
  if (!parts) {
    return fallback;
  }

  return getDateFromMinutes(parts.hour * 60 + parts.minute);
}

function getTimePartsFromDate(
  date: Date,
  timeZone?: string,
): { hour: number; minute: number } | null {
  if (timeZone) {
    try {
      const dtf = new Intl.DateTimeFormat('en-US', {
        timeZone,
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric',
        hour12: false,
      });
      const values: Record<string, string> = {};
      for (const part of dtf.formatToParts(date)) {
        if (part.type !== 'literal') {
          values[part.type] = part.value;
        }
      }
      const hour = Number(values.hour);
      const minute = Number(values.minute);
      if (Number.isFinite(hour) && Number.isFinite(minute)) {
        return { hour, minute };
      }
    } catch {
      // zona local del navegador
    }
  }

  const hour = date.getHours();
  const minute = date.getMinutes();
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) {
    return null;
  }
  return { hour, minute };
}

//  Convierte minutos a formato HH:MM
export function getDateFromMinutes(minutes: number): string {
  const total = Math.trunc(Number(minutes) || 0);
  const sign = total < 0 ? '-' : '';
  const absTotal = Math.abs(total);
  const hour = Math.floor(absTotal / 60);
  const min = absTotal % 60;
  return `${sign}${hour.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`;
}


// Obtiene hora astronómica
export function getAstroTime(astroData: any, type: string): string {
  const stat = astroData?.stat;
  const cfg = astroData?.cfg;
  if (!stat) {
    return '--:--';
  }

if (type === 'orto') {
  if (cfg?.sunrise_corr == 0) {
    return formatUnixTime(stat.sunrise);
  } else {
    return formatUnixTime(stat.sunrise_with_correction);
  }
}

if (type === 'ocaso') {
  if (cfg?.sunset_corr == 0) {
    return formatUnixTime(stat.sunset);
  } else {
    return formatUnixTime(stat.sunset_with_correction);
  }
}

return '--:--';
}


// Obtiene hora astronómica ajustada
export function getAstroAdjusted(astroData: any, type: string, offsetTime: string, offsetSign: string): string {
  const stat = astroData?.stat;
  const cfg = astroData?.cfg;
  if (!stat) {
    return '--:--';
  }

  const offsetMinutos = timeToMinutes(offsetTime);
  let hora = '--:--';
  if (type === 'orto') {
    if (cfg?.sunrise_corr == 0) {
      hora = formatUnixTime(stat.sunrise);
    } else {
      hora = formatUnixTime(stat.sunrise_with_correction);
    }
  } else {
    if (cfg?.sunset_corr == 0) {
      hora = formatUnixTime(stat.sunset);
    } else {
      hora = formatUnixTime(stat.sunset_with_correction);
    }
  }
    const horaMinutos = timeToMinutes(hora);
    const ajusteMinutos = offsetSign === '-' ? horaMinutos - offsetMinutos : horaMinutos + offsetMinutos;
    return getDateFromMinutes(ajusteMinutos);
}

/** Path SVG del interruptor de relé (cerrado = on, abierto = off), igual que en la tabla de programaciones. */
export function getRelaySwitchPath(action: unknown): string {
  return String(action ?? '').toLowerCase() === 'on'
    ? 'M3 14 H21'
    : 'M3 14 H9 L16 9 M16 14 H21';
}



// ************* LUM PERIODS *************

export const LUM_PERIOD_ID_MIN = 0;
export const LUM_PERIOD_ID_MAX = 5;

export function isValidLumPeriodId(id: unknown): boolean {
  const n = Number(id);
  return Number.isFinite(n) && n >= LUM_PERIOD_ID_MIN && n <= LUM_PERIOD_ID_MAX;
}

/** Ids 0–5 seleccionados en una programación (sin duplicados, ordenados). */
export function normalizeLumSchedulePeriodIds(raw: unknown): number[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  const ids = raw
    .map((item) => Number(item))
    .filter((id) => isValidLumPeriodId(id));
  return [...new Set(ids)].sort((a, b) => a - b);
}

export function findFreeLumPeriodId(periods: any[], maxPeriods: number): number | null {
  for (let i = 0; i < maxPeriods; i++) {
    if (!periods.some((item) => Number(item.id) === i)) {
      return i;
    }
  }
  return null;
}

/** Color del badge por ID de periodo (1–6), alineado con MAX_PERIODS del scheduler LUM. */
export function getLumPeriodBadgeClass(periodId: number): string {

  let tone=''
  switch (periodId) {
    case 0:
      tone = 'primary';
      break;
    case 1:
      tone = 'success';
      break;
    case 2:
      tone = 'danger';
      break;
    case 3:
      tone = 'secondary';
      break;
    case 4:
      tone = 'info';
      break;
    case 5:
      tone = 'warning';
      break;
    default:
      tone = 'secondary';
      break;
  }
  return `bg-${tone} text-white`;
}

function getLumPeriodTone(periodId: number): string {
  switch (Number(periodId)) {
    case 0:
      return 'primary';
    case 1:
      return 'success';
    case 2:
      return 'danger';
    case 3:
      return 'secondary';
    case 4:
      return 'info';
    case 5:
      return 'warning';
    default:
      return 'secondary';
  }
}

/** Variables CSS para calendario / resumen según id de periodo (1–6). */
export function getLumPeriodCalendarStyles(periodId: number): Record<string, string> {
  const tone = getLumPeriodTone(periodId);
  const isWarning = tone === 'warning';
  return {
    '--lum-period-rgb': `var(--bs-${tone}-rgb)`,
    '--lum-period-color': `var(--bs-${tone})`,
    '--lum-period-inside-text': isWarning
      ? 'var(--bs-warning-text-emphasis, var(--bs-dark))'
      : `var(--bs-${tone})`,
    '--lum-period-edge-text': '#fff',
  };
}

function pad2(value: unknown): string {
  return String(Number(value) || 0).padStart(2, '0');
}

function formatMinutesToTimeStr(minutes: unknown): string {
  const total = Number(minutes);
  if (!Number.isFinite(total)) {
    return '00:00';
  }
  const safe = Math.max(0, Math.min(1439, Math.round(total)));
  const hours = Math.floor(safe / 60);
  const mins = safe % 60;
  return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
}

/** Texto desde/hasta para periodo API o borrador del modal (draft con startTimeStr). */
export function formatLumPeriodRange(period: any): string {
  if (!period) {
    return '';
  }

  const startDate = `${pad2(period.start_day)}/${pad2(period.start_month)}`;
  const endDate = `${pad2(period.end_day)}/${pad2(period.end_month)}`;

  const startMin = Number(period.start_min);
  const endMin = Number(period.end_min);
  const fullDays = period.scheduleMode === 'fullDays'
    || (startMin === 0 && Number.isFinite(endMin) && endMin >= 1439);

  if (fullDays) {
    return `${startDate} → ${endDate}`;
  }

  const startTime = period.startTimeStr ?? formatMinutesToTimeStr(period.start_min);
  const endTime = period.endTimeStr ?? formatMinutesToTimeStr(period.end_min);
  return `${startDate} · ${startTime} → ${endDate} · ${endTime}`;
}


/** Punto del histórico del relé (el API lo devuelve del más reciente al más antiguo). */
export interface LumRelayHistoryPoint {
  state: string;
  permanent: number;
  e: number;
  total_minutes?: number | null;
  partial_minutes?: number | null;
  maintenance_pending?: number | null;
  total_hours?: number | null;
  timestamp: string;
}

/** Registro del histórico, en el mismo orden que devuelve la API. */
export interface AstrolumHistoryRecord {
  state: string;
  at: string;
  minutes: number;
  energyKwh: number;
}

/** Ciclo del histórico. Los registros van en el orden de la API; si no hay OFF, el ciclo sigue abierto. */
export interface AstrolumOnOffSession {
  records: AstrolumHistoryRecord[];
  durationSeconds: number;
  energyKwh: number;
}

/** Todos los registros, en el orden de la API. Un `off` seguido de su `on` forma el intervalo. */
export function formatLumHistoryDate(
  history: LumRelayHistoryPoint[] | null | undefined,
): AstrolumOnOffSession[] {
  const points = history ?? [];
  const sessions: AstrolumOnOffSession[] = [];

  for (let index = 0; index < points.length; index++) {
    const point = points[index];
    const older = points[index + 1];
    if (point.state === 'off' && older?.state === 'on') {
      sessions.push({
        records: [toHistoryRecord(point), toHistoryRecord(older)],
        durationSeconds: secondsBetween(point.timestamp, older.timestamp),
        energyKwh: Math.max(0, counter(point.e) - counter(older.e)),
      });
      index++;
      continue;
    }
    sessions.push({
      records: [toHistoryRecord(point)],
      durationSeconds: 0,
      energyKwh: 0,
    });
  }

  return sessions;
}

function toHistoryRecord(point: LumRelayHistoryPoint): AstrolumHistoryRecord {
  const minutes = point.total_minutes != null
    ? counter(point.total_minutes)
    : counter(point.total_hours) * 60;
  return {
    state: point.state,
    at: point.timestamp,
    minutes,
    energyKwh: counter(point.e),
  };
}

function counter(value: unknown): number {
  return Number(value) || 0;
}

function secondsBetween(later: string, earlier: string): number {
  const seconds = Math.round((new Date(later).getTime() - new Date(earlier).getTime()) / 1000);
  return Math.max(0, seconds) || 0;
}
